import BuildIcon from '@mui/icons-material/Build'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import { alpha, Box, LinearProgress, Typography, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import { useRoadmap } from './RoadmapContext'
import { PhaseSummary, getPhaseShortLabel } from './types'

const MONTH_ABBRS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const PhaseTimeline: React.FC = () => {
  const { phaseSummaries, state, dispatch } = useRoadmap()

  // Compute calendar labels from engagement start month
  const phaseMonthLabels = useMemo(() => {
    const labels: Record<string, string> = {}
    const startMonth = state.engagementConfig.startMonth // YYYY-MM
    if (!startMonth) return labels
    const [yr, mo] = startMonth.split('-').map(Number) // yr=2026, mo=4 for "2026-04"
    for (const s of phaseSummaries) {
      if (s.phase === 'setup') continue
      const monthNum = parseInt(s.phase.replace('month-', ''), 10)
      const totalMonths = (yr * 12 + (mo - 1)) + (monthNum - 1)
      const calYear = Math.floor(totalMonths / 12)
      const calMonth = totalMonths % 12
      labels[s.phase] = `${MONTH_ABBRS[calMonth]} ${calYear}`
    }
    return labels
  }, [state.engagementConfig.startMonth, phaseSummaries])

  return (
    <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', py: 1, mb: 2 }}>
      {phaseSummaries.map(summary => (
        <PhaseTab
          key={summary.phase}
          summary={summary}
          isActive={state.activePhase === summary.phase}
          monthLabel={phaseMonthLabels[summary.phase]}
          onClick={() => dispatch({ type: 'SET_ACTIVE_PHASE', phase: summary.phase })}
        />
      ))}
    </Box>
  )
}

function PhaseTab({
  summary,
  isActive,
  monthLabel,
  onClick,
}: {
  summary: PhaseSummary
  isActive: boolean
  monthLabel?: string
  onClick: () => void
}) {
  const theme = useTheme()
  const hasItems = summary.itemCount > 0
  const fillPct = Math.min(summary.utilization, 100)

  // Color logic matching TED Toolbox
  const accentColor = summary.isOverBudget ? theme.palette.text.error : theme.palette.text.complete
  const borderColor = isActive
    ? accentColor
    : summary.isOverBudget
      ? alpha(theme.palette.text.error, 0.3)
      : hasItems
        ? alpha(theme.palette.text.complete, 0.2)
        : alpha(theme.palette.text.primary, 0.15)

  const bgColor = isActive
    ? summary.isOverBudget
      ? alpha(theme.palette.text.error, 0.08)
      : alpha(theme.palette.text.complete, 0.08)
    : 'transparent'

  const PhaseIcon =
    summary.phase === 'setup'
      ? BuildIcon
      : summary.strategicCount > summary.executableCount
        ? TrendingUpIcon
        : FlashOnIcon

  return (
    <Box
      onClick={onClick}
      sx={{
        flex: '1 1 80px',
        minWidth: 80,
        p: 1,
        borderRadius: 1.5,
        cursor: 'pointer',
        border: '1px solid',
        borderColor,
        bgcolor: bgColor,
        transition: 'all 0.2s',
        boxShadow: isActive
          ? `0 0 0 1px ${accentColor}40, 0 0 12px ${accentColor}25`
          : 'none',
        '&:hover': { borderColor: accentColor, bgcolor: `${accentColor}08` },
      }}
    >
      {/* Label + points */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
        <PhaseIcon
          sx={{
            fontSize: 14,
            color: isActive || hasItems ? accentColor : 'text.disabled',
            opacity: isActive || hasItems ? 1 : 0.6,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: 11,
            color: isActive ? accentColor : hasItems ? 'text.primary' : 'text.disabled',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getPhaseShortLabel(summary.phase)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            ml: 'auto',
            fontSize: 10,
            fontVariantNumeric: 'tabular-nums',
            color: summary.isOverBudget ? theme.palette.text.error : 'text.secondary',
            flexShrink: 0,
          }}
        >
          {summary.usedPoints}/{summary.budget}
        </Typography>
      </Box>

      {/* Month label (e.g. "Apr 2026") */}
      {monthLabel && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontSize: 9,
            color: 'text.disabled',
            mb: 0.5,
            pl: 2.5,
          }}
        >
          {monthLabel}
        </Typography>
      )}

      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={fillPct}
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: summary.isOverBudget
            ? alpha(theme.palette.text.error, 0.12)
            : hasItems
              ? alpha(theme.palette.text.complete, 0.12)
              : alpha(theme.palette.text.primary, 0.1),
          '& .MuiLinearProgress-bar': {
            borderRadius: 2,
            bgcolor: summary.isOverBudget
              ? theme.palette.text.error
              : hasItems
                ? theme.palette.text.complete
                : 'text.disabled',
          },
        }}
      />
    </Box>
  )
}

export default PhaseTimeline
